import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { EntitySelector } from '@bingads-webui-react/entity-selector';
import { submitEvent } from '@bingads-webui-universal/accessibility-helper';
import { getWithResultContainerStyles } from './styles';

const DEFAULT_RESULT_PAGESIZE = 6;

/**
 * result container component for the multi selector
 */

class OriginMultiSelectorResultContainer extends React.Component {
  static defaultProps = {
    resultListAriaLabel: '',
  };
  constructor(props) {
    super(props);
    this.state = {
      currentPageNumber: 0,
    };

    this.pageSize = props.resultContainerOptions.pageSize ?
      props.resultContainerOptions.pageSize : DEFAULT_RESULT_PAGESIZE;
  }

  /**
   * the result container's data is a local data, do not need net fetch
   * when fetch the new page items, update the pageNumber and will recompute the new list
   * @param {number} pageNumber - pageNumber to fetch
   * @returns {undefined} update the state: currentPageNumber
   */
  onFetch = ({ pageNumber = 0 } = {}) => {
    this.setState({
      currentPageNumber: pageNumber,
    });
  };

  /**
   * while click the remove all btn, should empty the resultlist
   * @returns {undefined} call the props callback updateResultList function: empty the resultlist
   */
  onRemoveAllClick = () => {
    const { updateResultList, onRemoveAll } = this.props.resultContainerOptions;
    updateResultList([]);
    if (onRemoveAll) {
      onRemoveAll();
    }
  };

  /**
   * On select item, update the result list and trigger onSelect callback
   * @param {item} item - item added
   * @returns {undefined} call the props callback updateResultList function: add the item to resultlist and trigger onSelect callback
   */
  onRemove = (item) => {
    const { updateResultList, onRemove } = this.props.resultContainerOptions;
    updateResultList([item]);
    if (onRemove) {
      onRemove(item);
    }
  };
  /**
   * the result container's data is a local data, do not need net fetch
   * the current items could be computed by the pagenumber and pagesize
   * @returns {Object[]} current page result list
   */
  getCurrentPageList() {
    const { resultList } = this.props.resultContainerOptions;
    let startIndex = this.pageSize * this.state.currentPageNumber;
    let pageNumber = this.state.currentPageNumber;
    if (startIndex >= resultList.length && pageNumber > 0) {
      while (startIndex >= resultList.length && pageNumber > 0) {
        pageNumber -= 1;
        startIndex -= this.pageSize;
      }
      this.setState({
        currentPageNumber: pageNumber,
      });
    }
    return resultList.slice(startIndex, startIndex + this.pageSize);
  }

  /**
   * render component for the selected result component header
   * @returns {object} return the header component
   */
  renderSelectedHeader = () => {
    const { renderHeader, resultList } = this.props.resultContainerOptions;
    if (renderHeader) {
      return renderHeader(resultList.length);
    }
    return null;
  };

  /**
   * this result container component should show the action bar： remove all
   * @returns {object} return the action bar component
   */
  renderActionBar = () => (
    <div
      className="remove-all-content"
      onClick={this.onRemoveAllClick}
      onKeyDown={submitEvent(() => this.onRemoveAllClick())}
      role="menuitem"
      tabIndex={0}
    >
      <span className="remove arrow-button" />
      <span>{this.props.i18n.getString('Remove all')}</span>
    </div>
  );

  render() {
    const {
      i18n, resultContainerOptions, classes, resultListAriaLabel,
    } = this.props;

    return (
      <div className={classNames('multi-selector-result-container', classes.multiSelectorResultContainer)}>
        <EntitySelector
          onFetch={this.onFetch}
          onSelect={this.onRemove}
          items={this.getCurrentPageList()}
          totalCount={resultContainerOptions.resultList.length}
          i18n={i18n}
          renderHeader={this.renderSelectedHeader}
          renderItem={resultContainerOptions.renderItem}
          renderEmptyList={resultContainerOptions.renderEmptyList}
          renderActionBar={this.renderActionBar}
          enableArrowKeysSupport
          pageSize={this.pageSize}
          pageNumber={this.state.currentPageNumber}
          selectedItem={null}
          isShowSearchbox={false}
          ariaLabel={resultListAriaLabel}
        />
      </div>
    );
  }
}

OriginMultiSelectorResultContainer.propTypes = {
  /**
    * JSS injected classes
    */
  classes: PropTypes.objectOf(PropTypes.any).isRequired,
  /**
     * i18n for localization
     */
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
    parseDecimal: PropTypes.func.isRequired,
  }).isRequired,
  /**
    * options for result container
    */
  resultContainerOptions: PropTypes.shape({
    /**
      * customized header style for result container, when omitted, will not render the header
      */
    renderHeader: PropTypes.func,
    /**
      * customized header style for result container, required
      */
    renderItem: PropTypes.func.isRequired,
    /**
      * customized empty list style for result container,
      * when omitted, will use the default style
      */
    renderEmptyList: PropTypes.func,
    /**
      * page size, required
      */
    pageSize: PropTypes.number,
    /**
     * all the items selected
     */
    resultList: PropTypes.arrayOf(PropTypes.any).isRequired,
    /**
     * callback for updating the resultList
     */
    updateResultList: PropTypes.func.isRequired,
    /**
    * callback function that is called select action is performed
    */
    onRemove: PropTypes.func,
    /**
    * callback function that is called remove all is performed
    */
    onRemoveAll: PropTypes.func,
  }).isRequired,

  /**
    * aria-label for result campaign list
    */
  resultListAriaLabel: PropTypes.string,
};

export const MultiSelectorResultContainer =
  withDefaultStyles(OriginMultiSelectorResultContainer, getWithResultContainerStyles);
